/**
 * useProfileForm Hook - Manages user profile form state and operations
 */
import { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../stores/authStore';
import userService from '../services/userService';
import { msg } from '../utils/messages';
import { mascaraTelefone } from '../lib/utils';

interface ProfileFormData {
  full_name: string;
  email: string;
  telefone: string;
}

interface NotificationSettings {
  emailNotif: boolean;
  pushNotif: boolean;
}

interface UseProfileFormReturn {
  formData: ProfileFormData;
  notificationSettings: NotificationSettings;
  isLoadingProfile: boolean;
  isSavingProfile: boolean;
  isSavingNotification: boolean;
  profileError: string | null;
  profileSuccess: string | null;
  handleProfileInputChange: (field: string, value: string) => void;
  handleNotificationChange: (field: string, value: boolean) => Promise<void>;
  handleSaveProfile: () => Promise<void>;
  getUserInitials: () => string;
  loadUserProfile: () => Promise<void>;
}

// Validate email format
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone format (Brazilian)
const validatePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 11;
};

export function useProfileForm(): UseProfileFormReturn {
  const { user } = useAuthStore();

  // Profile form state
  const [formData, setFormData] = useState<ProfileFormData>({
    full_name: '',
    email: '',
    telefone: ''
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotif: true,
    pushNotif: true
  });

  // Loading and feedback states
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

  // Load user profile data
  const loadUserProfile = useCallback(async () => {
    if (!user?.id) return;

    setIsLoadingProfile(true);
    setProfileError(null);

    try {
      const profile = await userService.getUser(user.id);
      const full_name = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
      setFormData({
        full_name: full_name || '',
        email: profile.email || '',
        telefone: profile.telefone ? mascaraTelefone(profile.telefone) : ''
      });
      setNotificationSettings(prev => ({
        ...prev,
        emailNotif: profile.receber_email ?? false
      }));
    } catch (error: any) {
      console.error('Error loading user profile:', error);
      setProfileError('Erro ao carregar perfil do usuário');
      msg.error('Erro ao carregar perfil do usuário');
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user?.id]);

  // Fetch user profile on mount
  useEffect(() => {
    if (user?.id) {
      loadUserProfile();
    }
  }, [user?.id, loadUserProfile]);

  // Validate profile form
  const validateProfileForm = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!formData.full_name || formData.full_name.trim().length < 2) {
      errors.push('Nome Completo deve ter pelo menos 2 caracteres');
    }

    if (!formData.email || !validateEmail(formData.email)) {
      errors.push('Email inválido');
    }

    if (formData.telefone && !validatePhone(formData.telefone)) {
      errors.push('Telefone inválido (use formato: (11) 98765-4321 ou 11987654321)');
    }

    return { isValid: errors.length === 0, errors };
  }, [formData]);

  // Handle profile save
  const handleSaveProfile = useCallback(async () => {
    if (!user?.id) {
      msg.error('Usuário não autenticado');
      return;
    }

    setProfileError(null);
    setProfileSuccess(null);

    const validation = validateProfileForm();
    if (!validation.isValid) {
      setProfileError(validation.errors.join('. '));
      msg.error(validation.errors[0]);
      return;
    }

    setIsSavingProfile(true);

    try {
      const nameParts = formData.full_name.trim().split(/\s+/);
      const first_name = nameParts[0];
      const last_name = nameParts.slice(1).join(' ');

      const updatePayload = {
        first_name,
        last_name,
        email: formData.email,
        telefone: formData.telefone
      };

      await userService.updateUser(user.id, updatePayload);

      useAuthStore.getState().updateUser({
        ...user,
        first_name,
        last_name,
        email: formData.email
      });

      setProfileSuccess('Perfil atualizado com sucesso!');
      msg.success('Perfil atualizado com sucesso!');

      setTimeout(() => {
        setProfileSuccess(null);
      }, 3000);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar perfil';
      setProfileError(errorMessage);
      msg.error(errorMessage);
    } finally {
      setIsSavingProfile(false);
    }
  }, [user, formData, validateProfileForm]);

  // Handle form input changes
  const handleProfileInputChange = useCallback((field: string, value: string) => {
    const maskedValue = field === 'telefone' ? mascaraTelefone(value) : value;

    setFormData(prev => ({
      ...prev,
      [field]: maskedValue
    }));

    if (profileError) {
      setProfileError(null);
    }
  }, [profileError]);

  // Handle notification settings change
  const handleNotificationChange = useCallback(async (field: string, value: boolean) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }));

    if (field === 'emailNotif' && user?.id) {
      setIsSavingNotification(true);
      try {
        await userService.updateUser(user.id, { receber_email: value });
        msg.success(value ? 'Notificações por email ativadas!' : 'Notificações por email desativadas!');
      } catch (error: any) {
        console.error('Erro ao salvar preferência de notificação:', error);
        msg.error('Erro ao salvar preferência de notificação');
        setNotificationSettings(prev => ({ ...prev, [field]: !value }));
      } finally {
        setIsSavingNotification(false);
      }
    }
  }, [user?.id]);

  // Get user initials for avatar
  const getUserInitials = useCallback((): string => {
    if (!formData.full_name) return '??';
    const parts = formData.full_name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    const first = parts[0][0]?.toUpperCase() || '';
    const last = parts[parts.length - 1][0]?.toUpperCase() || '';
    return first + last || '??';
  }, [formData.full_name]);

  return {
    formData,
    notificationSettings,
    isLoadingProfile,
    isSavingProfile,
    isSavingNotification,
    profileError,
    profileSuccess,
    handleProfileInputChange,
    handleNotificationChange,
    handleSaveProfile,
    getUserInitials,
    loadUserProfile,
  };
}
